import {
  ActionIcon,
  Box,
  Button,
  createStyles,
  Flex,
  Group,
  Header as MantineHeader,
  Indicator,
  Paper,
  Skeleton,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconBell,
  IconBellFilled,
  IconBellRinging,
  IconBriefcase,
  IconBuildingStore,
  IconCircleCheck,
  IconCurrencyRupee,
  IconFileInvoice,
  IconLink,
  IconLogout,
  IconMessageChatbot,
  IconPackage,
  IconPhoneCall,
  IconReceipt,
  IconSearch,
  IconTruckDelivery,
  IconWeight,
  IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMyAccountDetails } from "api/auth";
import { useNotificationsAll } from "api/notifications";
import {
  AppProduct,
  MukundaProductData,
  SaltInfoData,
  useMukundaAutocomplete,
} from "api/product-search";
import { useGetWareHousesInit } from "api/ware-houses";
import {
  NoResultsFound,
  ProductResults,
  SaltInfoResults,
} from "components/SearchResults";
import { onMessage } from "firebase/messaging";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RootStoreContext } from "stores/RootStore";
import { NotificationType } from "types/notifications";

import api from "../../axios.config";
import { messaging } from "../../firebase";

const useStyles = createStyles((theme) => ({
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "100%",
    paddingTop: 10,
    paddingLeft: 10,
    paddingRight: 20,
    paddingBottom: 10,
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 20,
  },
  hideMobile: {
    [theme.fn.smallerThan("md")]: {
      display: "none",
    },
  },
}));

// Flexible search matching function that handles partial matches
// e.g., "amoxycillin + cl" matches "amoxycillin 400mg/5ml + clavulanic acid 57mg/5ml"
const matchesSearchTerm = (text: string, searchTerm: string): boolean => {
  if (!searchTerm) return true;
  const lowerText = text.toLowerCase();
  const lowerSearch = searchTerm.toLowerCase().trim();

  // First try exact substring match (fastest)
  if (lowerText.includes(lowerSearch)) {
    return true;
  }

  // If exact match fails, try flexible matching:
  // Split search term by spaces and check if all parts appear in order
  const searchParts = lowerSearch
    .split(/\s+/)
    .filter((part) => part.trim().length > 0);

  if (searchParts.length === 0) return false;

  let lastIndex = 0;
  for (const part of searchParts) {
    const trimmedPart = part.trim();
    if (trimmedPart.length === 0) continue;

    // Find the part in the text starting from where we left off
    // This allows "cl" to match "clavulanic" (starts with "cl")
    const index = lowerText.indexOf(trimmedPart, lastIndex);

    if (index === -1) {
      return false; // Part not found
    }

    // Update lastIndex to continue searching from after this match
    lastIndex = index + trimmedPart.length;
  }

  return true; // All parts found in order
};

const Header = observer(() => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { authStore } = React.useContext(RootStoreContext);
  const { classes } = useStyles();

  const { data: accountData, isFetching: accountDataLoading } =
    useMyAccountDetails();
  const { data: wareHousesData } = useGetWareHousesInit();

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track the cached search term and data
  const [cachedSearchTerm, setCachedSearchTerm] = useState<string>("");
  const [cachedAutocompleteData, setCachedAutocompleteData] = useState<{
    saltInfoInfo: SaltInfoData[];
    mukundaProductData: MukundaProductData[];
  } | null>(null);

  // Determine if we should make an API call
  const shouldFetchFromAPI = useMemo(() => {
    if (debouncedSearch.length === 0) return false;
    if (debouncedSearch.length < 3) return false;

    // Check if we have cached data and if current search can use it
    const hasCachedData =
      cachedAutocompleteData && cachedSearchTerm.length >= 3;
    const canUseCache =
      hasCachedData &&
      debouncedSearch.toLowerCase().startsWith(cachedSearchTerm.toLowerCase());

    // If we can use cache, check if cached results actually have matches
    // If matches found in cache, don't make API call (no spelling mistake)
    if (canUseCache && cachedAutocompleteData) {
      const hasMatchingSalts = cachedAutocompleteData.saltInfoInfo.some(
        (salt) => matchesSearchTerm(salt.name, debouncedSearch)
      );
      const hasMatchingProducts =
        cachedAutocompleteData.mukundaProductData.some((product) => {
          return (
            matchesSearchTerm(product.productName, debouncedSearch) ||
            matchesSearchTerm(product.manufacturerName, debouncedSearch) ||
            matchesSearchTerm(product.saltInfo, debouncedSearch)
          );
        });

      // If we have matches in cache, use cache (no API call needed)
      if (hasMatchingSalts || hasMatchingProducts) {
        return false; // Don't make API call - use cached data
      }

      // If no matches in cache, don't make API call (likely a spelling mistake)
      // Just use cached data which will show no results
      return false;
    }

    // Make API call if:
    // 1. Search is exactly 3 characters AND we can't use cached data
    // 2. Search is longer than 3 AND we can't use cached data (different search term)
    if (debouncedSearch.length === 3) {
      return !canUseCache; // Make API call if we can't use cache
    }

    // For searches longer than 3, use cached data if current search starts with cached term
    // Otherwise, make API call (different search term)
    return !canUseCache;
  }, [debouncedSearch, cachedSearchTerm, cachedAutocompleteData]);

  const {
    data: autocompleteData,
    isLoading: isAutocompleteLoading,
    error: autocompleteError,
  } = useMukundaAutocomplete(
    shouldFetchFromAPI ? debouncedSearch : cachedSearchTerm,
    shouldFetchFromAPI && debouncedSearch.length >= 3
  );

  // Only show loading when we're actually fetching from API, not when filtering cached data
  const isLoading = shouldFetchFromAPI && isAutocompleteLoading;

  // Check for API errors (especially 500 errors)
  const hasError = autocompleteError && shouldFetchFromAPI;
  const errorStatus = autocompleteError?.response?.status;
  const isServerError =
    hasError &&
    errorStatus !== undefined &&
    errorStatus >= 500 &&
    errorStatus < 600;

  // Log 500 errors for debugging
  useEffect(() => {
    if (isServerError) {
      // eslint-disable-next-line no-console
      console.error("Search API Error:", {
        status: autocompleteError?.response?.status,
        searchTerm: debouncedSearch,
        error: autocompleteError,
      });
    }
  }, [isServerError, autocompleteError, debouncedSearch]);

  // Clear cache when search is cleared
  useEffect(() => {
    if (debouncedSearch.length === 0) {
      setCachedSearchTerm("");
      setCachedAutocompleteData(null);
    }
  }, [debouncedSearch]);

  // Update cache when we get new data from API
  useEffect(() => {
    if (autocompleteData?.body && shouldFetchFromAPI) {
      setCachedSearchTerm(debouncedSearch);
      setCachedAutocompleteData({
        saltInfoInfo: autocompleteData.body.saltInfoInfo || [],
        mukundaProductData: autocompleteData.body.mukundaProductData || [],
      });
    }
  }, [autocompleteData, shouldFetchFromAPI, debouncedSearch]);

  // Get filtered data based on current search
  const filteredAutocompleteData = useMemo(() => {
    // If search is less than 3 characters, return null
    if (debouncedSearch.length < 3) {
      return null;
    }

    // If we don't have cached data, return API data
    if (!cachedAutocompleteData) {
      return autocompleteData?.body || null;
    }

    // If we're using cached data (not fetching from API), filter it based on current search
    if (!shouldFetchFromAPI && debouncedSearch.length >= 3) {
      const filteredSalts = cachedAutocompleteData.saltInfoInfo.filter((salt) =>
        matchesSearchTerm(salt.name, debouncedSearch)
      );

      const filteredProducts = cachedAutocompleteData.mukundaProductData.filter(
        (product) => {
          return (
            matchesSearchTerm(product.productName, debouncedSearch) ||
            matchesSearchTerm(product.manufacturerName, debouncedSearch) ||
            matchesSearchTerm(product.saltInfo, debouncedSearch)
          );
        }
      );

      return {
        saltInfoInfo: filteredSalts,
        mukundaProductData: filteredProducts,
      };
    }

    // Return API data if we just fetched it
    return autocompleteData?.body || null;
  }, [
    cachedAutocompleteData,
    debouncedSearch,
    shouldFetchFromAPI,
    autocompleteData,
  ]);
  useEffect(() => {
    if (debouncedSearch.length > 0) {
      setDropdownOpen(true);
    } else {
      setDropdownOpen(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    setDropdownOpen(false);
  }, [location.pathname]);

  // Cart functionality removed

  const { data: notificationList, refetch } = useNotificationsAll({
    notificationType: "UNREAD",
    search: "",
    page: 0,
    pageSize: 6,
  });

  const showNotificationIndicator = !!notificationList?.body?.totalResults;

  const getNotificationIcon = (
    notificationTypeParam: NotificationType | undefined
  ) => {
    const iconSize = 16;
    const iconProps = { size: iconSize, stroke: 1.5 };

    if (!notificationTypeParam) return <IconBellFilled {...iconProps} />;

    const iconMap: Record<NotificationType, React.ReactNode> = {
      ORDERS: <IconBell {...iconProps} />,

      B2B_ORDER: <IconBriefcase {...iconProps} />,

      B2C_ORDER: <IconBell {...iconProps} />,

      PAYMENT: <IconCurrencyRupee {...iconProps} />,

      PICKUPS: <IconPackage {...iconProps} />,

      DELIVERY_DELIVERED: <IconCircleCheck {...iconProps} />,

      DELIVERY_COLLECTED: <IconTruckDelivery {...iconProps} />,

      DELIVERY_BOY_ALERT: <IconAlertTriangle {...iconProps} />,

      STORE_CLOSED: <IconBuildingStore {...iconProps} />,

      CALL_REQUEST: <IconPhoneCall {...iconProps} />,

      INVOICE: <IconReceipt {...iconProps} />,

      HEAVY_WEIGHT: <IconWeight {...iconProps} />,

      CONNECTION_REQUEST: <IconLink {...iconProps} />,

      RING2ALERT: <IconBellRinging {...iconProps} />,

      GENERAL: <IconBell {...iconProps} />,
      CART: <IconBell {...iconProps} />,
    };

    return iconMap[notificationTypeParam] || <IconBellFilled {...iconProps} />;
  };

  const getNotificationColor = (
    notificationTypeParam: NotificationType | undefined
  ) => {
    if (!notificationTypeParam) return "yellow";

    const colorMap: Record<NotificationType, string> = {
      PAYMENT: "rgb(16 185 129)",
      STORE_CLOSED: "black",
      DELIVERY_BOY_ALERT: "rgb(6 182 212)",
      HEAVY_WEIGHT: "rgb(239 68 68)",
      ORDERS: "rgb(37 99 235)",
      B2B_ORDER: "rgb(37 99 235)",
      B2C_ORDER: "rgb(147 51 234)",
      DELIVERY_DELIVERED: "rgb(34 197 94)",
      DELIVERY_COLLECTED: "rgb(249 115 22)",
      PICKUPS: "teal",
      CALL_REQUEST: "rgb(99 102 241)",
      INVOICE: "rgb(21, 128, 61)",
      CART: "#fab005",
      CONNECTION_REQUEST: "#fab005",
      RING2ALERT: "#fab005",
      GENERAL: "#fab005",
    };

    return colorMap[notificationTypeParam] || "#fab005";
  };

  useEffect(() => {
    onMessage(messaging, (payload) => {
      const notificationTypeParam =
        (payload.data?.type as NotificationType) || "GENERAL";
      const icon = getNotificationIcon(notificationTypeParam);
      const color = getNotificationColor(notificationTypeParam);

      notifications.show({
        icon: React.createElement(
          "div",
          {
            style: {
              width: "60px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            },
          },
          React.cloneElement(icon as React.ReactElement, {
            size: 25,
            stroke: 1.5,
            style: { color: "white" },
          })
        ),
        title: payload.notification?.title ?? "Notification",
        message: payload.notification?.body ?? "You have a new notification",
        color,
        autoClose: 5000,
        styles: () => ({
          root: {
            maxWidth: "480px",
            width: "400px",
            borderLeft: `4px solid ${color}`,
            borderRadius: "8px",
            marginRight: "40px",
            marginLeft: "20px",
          },
          icon: {
            width: "50px !important",
            height: "50px !important",
            marginRight: "1rem",
            borderRadius: "50%",
            flex: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `${color} !important`,
            "& > div": {
              backgroundColor: `${color} !important`,
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          },
          title: {
            fontSize: "16px",
            fontWeight: 600,
            color: "#1a1a1a",
          },
          description: {
            fontSize: "14px",
            color: "#666666",
          },
        }),
        onLoad: () => {
          refetch();
        },
      });
    });
  }, []);

  const activeWarehouse = wareHousesData?.body?.find((w) => w.isActive);
  const marketCityName = activeWarehouse?.market?.name || "";

  // Search prioritization logic for autocomplete data
  const prioritizeAutocompleteResults = (
    saltInfoData: SaltInfoData[],
    productData: MukundaProductData[],
    searchTerm: string
  ) => {
    if (!searchTerm)
      return {
        prioritizedSalts: saltInfoData,
        prioritizedProducts: productData,
      };

    const lowerSearchTerm = searchTerm.toLowerCase();

    // Prioritize salt info data
    const prioritizedSalts = saltInfoData
      .filter((salt) => {
        return matchesSearchTerm(salt.name, searchTerm);
      })
      .sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();

        // Exact match first
        if (aName === lowerSearchTerm) return -1;
        if (bName === lowerSearchTerm) return 1;

        // Starts with search term
        if (
          aName.startsWith(lowerSearchTerm) &&
          !bName.startsWith(lowerSearchTerm)
        )
          return -1;
        if (
          bName.startsWith(lowerSearchTerm) &&
          !aName.startsWith(lowerSearchTerm)
        )
          return 1;

        // Contains search term
        return aName.indexOf(lowerSearchTerm) - bName.indexOf(lowerSearchTerm);
      });

    // Prioritize products
    const prioritizedProducts = productData
      .filter((product) => {
        return (
          matchesSearchTerm(product.productName, searchTerm) ||
          matchesSearchTerm(product.manufacturerName, searchTerm) ||
          matchesSearchTerm(product.saltInfo, searchTerm)
        );
      })
      .sort((a, b) => {
        const aName = a.productName.toLowerCase();
        const bName = b.productName.toLowerCase();

        // Exact match first
        if (aName === lowerSearchTerm) return -1;
        if (bName === lowerSearchTerm) return 1;

        // Starts with search term
        if (
          aName.startsWith(lowerSearchTerm) &&
          !bName.startsWith(lowerSearchTerm)
        )
          return -1;
        if (
          bName.startsWith(lowerSearchTerm) &&
          !aName.startsWith(lowerSearchTerm)
        )
          return 1;

        // Contains search term
        return aName.indexOf(lowerSearchTerm) - bName.indexOf(lowerSearchTerm);
      });

    return { prioritizedSalts, prioritizedProducts };
  };

  // Get prioritized results from autocomplete data
  const prioritizedResults = filteredAutocompleteData
    ? prioritizeAutocompleteResults(
        filteredAutocompleteData.saltInfoInfo || [],
        filteredAutocompleteData.mukundaProductData || [],
        debouncedSearch
      )
    : { prioritizedSalts: [], prioritizedProducts: [] };

  // Handle search navigation
  const handleSearch = async () => {
    setDropdownOpen(false);
    setSearch("");
    if (search.trim()) {
      // Direct API call for network visibility
      try {
        await api.get(
          `${
            process.env.REACT_APP_API_MUKUNDA_SEARCH_RESULTS
          }?search=${encodeURIComponent(search.trim())}&page=0&pageSize=20`
        );
      } catch (e) {}
      navigate(`/appproducts?search=${encodeURIComponent(search.trim())}`);
    } else {
      navigate("/appproducts");
    }
  };

  // Handle Enter key press in search input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <>
      <MantineHeader
        sx={{
          position: "relative",
          marginBottom: " 0 !important",
          borderBottom: 0,
          transition: "transform 400ms ease",
          overflow: "visible",

          "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "var(--background-header)",
            zIndex: -1,
          },
        }}
        mb={120}
        height={60}
      >
        <div
          className={classes.container}
          style={{
            width: authStore.isAuthorized ? "calc(100% - 64px)" : "100%",
            marginLeft: authStore.isAuthorized ? "64px" : 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
            {/* Left section */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <UnstyledButton
                onClick={() => {
                  navigate("/dashboard");
                }}
                mt={marketCityName === "" ? 0 : 20}
              >
                <Text color="white" weight={600}>
                  Welcome {accountData?.body?.firstName}
                </Text>
                <Text color="white" weight={400} mb={5}>
                  {marketCityName}
                </Text>
              </UnstyledButton>
              {accountDataLoading && (
                <Skeleton width={100} height={12} radius="xl" color="white" />
              )}
            </div>

            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  maxWidth: 570,
                }}
              >
                <Group spacing={0} style={{ width: "100%" }}>
                  <TextInput
                    ref={inputRef}
                    icon={<IconSearch size={20} color="#b0b3bb" />}
                    placeholder="Search by product name or salt information..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.currentTarget.value);
                    }}
                    onKeyDown={handleKeyDown}
                    radius="md"
                    size="md"
                    style={{ flex: 1 }}
                    rightSection={
                      search.length > 0 && (
                        <ActionIcon
                          size="lg"
                          variant="subtle"
                          onClick={() => setSearch("")}
                          style={{ marginRight: 8 }}
                        >
                          <IconX size={20} />
                        </ActionIcon>
                      )
                    }
                    styles={{
                      input: {
                        height: 41,
                        fontSize: 14,
                        paddingLeft: 40,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                    }}
                  />
                  <Button
                    style={{
                      height: 41,
                      width: 120,
                      background: "#b80000",
                      fontWeight: 500,
                      fontSize: 16,
                      marginLeft: 0,
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      borderTopRightRadius: 8,
                      borderBottomRightRadius: 8,
                    }}
                    color="red"
                    radius="md"
                    onClick={handleSearch}
                  >
                    Search
                  </Button>
                </Group>
                {debouncedSearch.length > 0 && (
                  <Paper
                    shadow="md"
                    radius="md"
                    p={0}
                    style={{
                      position: "absolute",
                      top: "100%",

                      width: "100%",
                      zIndex: 2000,
                      minHeight: 80,
                      maxHeight: 500,
                      overflowY: "auto",
                      border: "1px solid #eee",
                    }}
                  >
                    {isLoading && (
                      <Box p="xl" ta="center">
                        Loading...
                      </Box>
                    )}
                    {!isLoading && hasError && (
                      <Box p="xl" ta="center">
                        <Text color="red" size="sm" weight={500}>
                          {isServerError
                            ? `Server Error (${errorStatus}): Please try again later`
                            : "Error loading search results. Please try again."}
                        </Text>
                      </Box>
                    )}
                    {!isLoading &&
                      !hasError &&
                      prioritizedResults.prioritizedSalts.length === 0 &&
                      prioritizedResults.prioritizedProducts.length === 0 && (
                        <NoResultsFound
                          searchTerm={debouncedSearch}
                          onPopularSearchClick={(searchTerm) => {
                            setSearch(searchTerm);
                          }}
                        />
                      )}
                    {!isLoading &&
                      !hasError &&
                      (prioritizedResults.prioritizedSalts.length > 0 ||
                        prioritizedResults.prioritizedProducts.length > 0) && (
                        <Box p="md">
                          {prioritizedResults.prioritizedSalts.length > 0 && (
                            <SaltInfoResults
                              saltFilters={prioritizedResults.prioritizedSalts}
                              searchTerm={debouncedSearch}
                              onSaltClick={(saltId, saltName) => {
                                setDropdownOpen(false);
                                setSearch("");
                                navigate(
                                  `/appproducts?saltInfoId=${encodeURIComponent(
                                    saltId
                                  )}&saltName=${encodeURIComponent(saltName)}`
                                );
                              }}
                            />
                          )}

                          {prioritizedResults.prioritizedSalts.length > 0 &&
                            prioritizedResults.prioritizedProducts.length >
                              0 && <Box mt="xl" />}

                          {prioritizedResults.prioritizedProducts.length >
                            0 && (
                            <ProductResults
                              products={
                                prioritizedResults.prioritizedProducts as (
                                  | AppProduct
                                  | MukundaProductData
                                )[]
                              }
                              searchTerm={debouncedSearch}
                              onProductClick={(productId) => {
                                setDropdownOpen(false);
                                setSearch("");
                                navigate(`/viewproducts/${productId}`);
                              }}
                              onAddToCart={(productId) => {
                                // Handle add to cart logic here
                                // eslint-disable-next-line no-console
                                console.log("Add to cart:", productId);
                              }}
                            />
                          )}
                        </Box>
                      )}
                  </Paper>
                )}
              </div>
            </div>

            {/* Right section */}
            <div
              className={classes.right}
              style={{ flex: 1, justifyContent: "flex-end" }}
            >
              <Flex direction="row" gap={10}>
                <Tooltip
                  label="MedicoStores AI"
                  withArrow
                  position="bottom"
                  className={classes.hideMobile}
                >
                  <Indicator
                    label="Ai"
                    size={16}
                    offset={3}
                    position="top-end"
                    color="red"
                    styles={{
                      indicator: {
                        fontSize: "10px",
                        fontWeight: 700,
                        padding: "0 4px",
                      },
                    }}
                  >
                    <ActionIcon
                      size={40}
                      radius={14}
                      variant="filled"
                      onClick={() => {
                        navigate("/ai-support");
                      }}
                      sx={(theme) => ({
                        color: theme.white,
                      })}
                    >
                      <IconMessageChatbot size={24} />
                    </ActionIcon>
                  </Indicator>
                </Tooltip>
                <Tooltip
                  label="Purchase Invoice"
                  withArrow
                  position="bottom"
                  className={classes.hideMobile}
                >
                  <ActionIcon
                    size={40}
                    radius={14}
                    variant="filled"
                    onClick={() => {
                      navigate("/purchases/invoices");
                    }}
                  >
                    <IconReceipt size={24} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip
                  label="Sales Invoice"
                  withArrow
                  position="bottom"
                  className={classes.hideMobile}
                >
                  <ActionIcon
                    size={40}
                    radius={14}
                    variant="filled"
                    onClick={() => {
                      navigate("/sales/instore");
                    }}
                  >
                    <IconFileInvoice size={24} />
                  </ActionIcon>
                </Tooltip>

                {/* <Tooltip label="Profile" withArrow position="bottom">
                  <ActionIcon
                    size={40}
                    radius={14}
                    variant="filled"
                    onClick={() => {
                      navigate("/profile");
                    }}
                  >
                    <IconUser size={24} />
                  </ActionIcon>
                </Tooltip> */}
                <Tooltip label="Notifications" withArrow position="bottom">
                  <Indicator
                    color="red"
                    disabled={!showNotificationIndicator}
                    offset={3}
                    size={16}
                    label={notificationList?.body?.totalResults}
                  >
                    <ActionIcon
                      size={40}
                      radius={14}
                      variant="filled"
                      onClick={() => {
                        navigate("/notifications");
                      }}
                    >
                      <IconBell size={24} />
                    </ActionIcon>
                  </Indicator>
                </Tooltip>

                <Tooltip label="Logout" withArrow position="bottom">
                  <ActionIcon
                    size={40}
                    radius={14}
                    variant="filled"
                    onClick={async () => {
                      queryClient.clear();

                      await authStore.logout();
                    }}
                  >
                    <IconLogout size={24} />
                  </ActionIcon>
                </Tooltip>
              </Flex>
            </div>
          </div>
        </div>
      </MantineHeader>
    </>
  );
});

export { Header };
